import { useCallback, useEffect, useRef } from "react";

export default function useHibidAuctionsLogic({
  orgReady,
  tab,
  apiBaseUrl,
  routes,
  apiFetchJson,
  handleNeedsAuth,
  toastOpen,
  fetchUsers,
  invalidateUsersCache,
  accountManagers,
  selectedAuction,
  selectedAuctionId,
  selectedAuctionLabel,
  auctionRegs,
  lotsAgg,
  lotsUser,
  createAuctionBusy,
  createAuctionName,
  createAuctionEndAtLocal,
  createAuctionConsignor,
  createAuctionLocation,
  createAuctionRegistrationFile,
  createAuctionLotsFile,
  setAuctions,
  setSelectedAuction,
  setAuctionSummary,
  setAuctionSummaryLoading,
  setAuctionRegs,
  setAregLoading,
  setAregSearch,
  setAregShipperFilter,
  setAregNewUserFilter,
  setAregPickupFilter,
  setAregShippedFilter,
  setAregConsignorFilter,
  setAregPickupDraftByEmail,
  setAregPickupSavingByEmail,
  setAregPickupEditByEmail,
  setAregShelfDraftByEmail,
  setAregShelfSavingByEmail,
  setAregAmountDueDraftByEmail,
  setAregSelected,
  setAuctionEndAtLocal,
  setAuctionConsignors,
  setAuctionAccountManagerIds,
  setAuctionPickupLocations,
  setAuctionPickupSlots,
  setAuctionLocation,
  setAuctionFieldOptions,
  setSyncInfo,
  setAuctionViewMode,
  setLotsAgg,
  setLotsOpen,
  setLotsBusy,
  setLotsErr,
  setLotsData,
  setLotsUser,
  setRegistrationFulfillmentConfirm,
  registrationFulfillmentConfirm,
  registrationFulfillmentConfirmBusy,
  setRegistrationFulfillmentConfirmBusy,
  setUploadBusy,
  setListernautUploadBusy,
  setShipBusy,
  setRegUploadBusy,
  setUploadDialogText,
  setCreateAuctionOpen,
  setCreateAuctionBusy,
  setCreateAuctionName,
  setCreateAuctionEndAtLocal,
  setCreateAuctionLocation,
  setCreateAuctionLocationInput,
  setCreateAuctionLocationCityOptions,
  setCreateAuctionRegistrationFile,
  setCreateAuctionLotsFile,
  setFileNameMismatchDialog,
  fileNameMismatchResolveRef,
  shippingFileInputRef,
  listernautFileInputRef,
  toAbsInt,
  safeNum,
  normalize,
  isEmail,
  safeToMillis,
  toInputDateTimeLocal,
  fromInputDateTimeLocalToMs,
  localDateKeyFromMs,
  getConsignorsFromAuction,
  getAuctionAccountManagerIds,
  getAuctionPickupLocationsFromAuction,
  buildAuctionPickupSlotDrafts,
  mergeAuctionFieldOptions,
  deriveAuctionFieldOptionsFromRows,
  getRegistrationPickupAppointment,
  getDisplayBidderName,
  getDisplayUsername,
  getLotPickupCounts,
  getLotShippedCounts,
  isShippingRequested,
  isXlsxFile,
  uniqSortedOptions,
  formatNumber2,
}) {
  const apiFetchJsonRef = useRef(apiFetchJson);
  const handleNeedsAuthRef = useRef(handleNeedsAuth);
  const toastOpenRef = useRef(toastOpen);
  const fetchUsersRef = useRef(fetchUsers);
  const invalidateUsersCacheRef = useRef(invalidateUsersCache);

  useEffect(() => {
    apiFetchJsonRef.current = apiFetchJson;
  }, [apiFetchJson]);
  useEffect(() => {
    handleNeedsAuthRef.current = handleNeedsAuth;
  }, [handleNeedsAuth]);
  useEffect(() => {
    toastOpenRef.current = toastOpen;
  }, [toastOpen]);
  useEffect(() => {
    fetchUsersRef.current = fetchUsers;
  }, [fetchUsers]);
  useEffect(() => {
    invalidateUsersCacheRef.current = invalidateUsersCache;
  }, [invalidateUsersCache]);

  const fetchAuctions = useCallback(async ({ force = false, sync = false } = {}) => {
    if (!orgReady) return [];
    void force;
    try {
      const qs = new URLSearchParams();
      qs.set("limit", "all");
      qs.set("sync", sync ? "true" : "false");
      const { res, data } = await apiFetchJsonRef.current(`${apiBaseUrl}${routes.auctions}?${qs.toString()}`);
      if (handleNeedsAuthRef.current(res, data)) {
        setSyncInfo(null);
        setAuctions([]);
        setAuctionFieldOptions({ consignors: [], locations: [] });
        return [];
      }
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Request failed (${res.status})`);
      const rows = Array.isArray(data?.rows) ? data.rows : [];
      setAuctions(rows);
      setAuctionFieldOptions(
        mergeAuctionFieldOptions(deriveAuctionFieldOptionsFromRows(rows), {
          consignors: data?.auctionFieldOptions?.consignors || [],
          locations: data?.auctionFieldOptions?.locations || [],
        })
      );
      setSyncInfo(data?.sync ?? null);
      return rows;
    } catch (e) {
      toastOpenRef.current(e?.message || "Failed to load auctions", "error");
      return [];
    }
  }, [
    apiBaseUrl,
    deriveAuctionFieldOptionsFromRows,
    mergeAuctionFieldOptions,
    orgReady,
    routes.auctions,
    setAuctionFieldOptions,
    setAuctions,
    setSyncInfo,
  ]);

  useEffect(() => {
    if (!orgReady || tab !== 1) return;
    void fetchAuctions({ force: false, sync: false });
  }, [fetchAuctions, orgReady, tab]);

  const fetchAuctionSummary = useCallback(async (auctionId, { force = false } = {}) => {
    if (!orgReady || !auctionId) return;
    void force;
    setAuctionSummaryLoading(true);
    try {
      const { res, data } = await apiFetchJsonRef.current(
        `${apiBaseUrl}${routes.auctionsSummary}?auctionId=${encodeURIComponent(auctionId)}`
      );
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Request failed (${res.status})`);
      setAuctionSummary(data);
      const endMs = safeToMillis(data?.auctionEndAtMs ?? data?.auctionEndAt);
      setAuctionEndAtLocal((prev) => (prev ? prev : endMs ? toInputDateTimeLocal(endMs) : ""));
      setAuctionConsignors(getConsignorsFromAuction(data));
      setAuctionAccountManagerIds((prev) =>
        prev.length ? prev : getAuctionAccountManagerIds(data, accountManagers)
      );
      setAuctionPickupLocations((prev) =>
        prev.length ? prev : getAuctionPickupLocationsFromAuction(data)
      );
      setAuctionPickupSlots((prev) =>
        prev.length ? prev : buildAuctionPickupSlotDrafts(data)
      );
      setAuctionLocation(String(data?.location || ""));
      setAuctionFieldOptions((prev) =>
        mergeAuctionFieldOptions(prev, {
          consignors: getConsignorsFromAuction(data),
          locations: data?.location ? [data.location] : [],
        })
      );
    } catch (e) {
      setAuctionSummary(null);
      toastOpenRef.current(e?.message || "Failed to load auction summary", "error");
    } finally {
      setAuctionSummaryLoading(false);
    }
  }, [
    accountManagers,
    apiBaseUrl,
    buildAuctionPickupSlotDrafts,
    getAuctionAccountManagerIds,
    getAuctionPickupLocationsFromAuction,
    getConsignorsFromAuction,
    mergeAuctionFieldOptions,
    orgReady,
    routes.auctionsSummary,
    safeToMillis,
    setAuctionAccountManagerIds,
    setAuctionConsignors,
    setAuctionEndAtLocal,
    setAuctionFieldOptions,
    setAuctionLocation,
    setAuctionPickupLocations,
    setAuctionPickupSlots,
    setAuctionSummary,
    setAuctionSummaryLoading,
    toInputDateTimeLocal,
  ]);

  const fetchAuctionRegsPage = useCallback(async ({ auctionId, sync = false, force = false }) => {
    if (!orgReady || !auctionId) return;
    void force;
    setAregLoading(true);
    try {
      const allRows = [];
      let cursorDocId = null;
      let page = 0;

      while (true) {
        const qs = new URLSearchParams();
        qs.set("auctionId", auctionId);
        qs.set("limit", "1000");
        qs.set("sync", sync && page === 0 ? "true" : "false");
        if (cursorDocId) qs.set("cursorDocId", String(cursorDocId));

        const { res, data } = await apiFetchJsonRef.current(`${apiBaseUrl}${routes.auctionRegistrations}?${qs.toString()}`);
        if (handleNeedsAuthRef.current(res, data)) {
          setAuctionRegs([]);
          return;
        }
        if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Request failed (${res.status})`);

        const rows = Array.isArray(data?.rows) ? data.rows : [];
        allRows.push(...rows);
        if (sync && page === 0) setSyncInfo(data?.sync ?? null);

        const nextCursor = data?.nextCursorDocId ?? data?.nextCursorEmailSentAt ?? null;
        const nextCursorStr = String(nextCursor || "").trim();
        if (!nextCursorStr || rows.length === 0 || nextCursorStr === String(cursorDocId || "")) break;
        cursorDocId = nextCursorStr;
        page += 1;
        if (page > 100) break;
      }

      setAuctionRegs(allRows);
      setAregSelected(new Set());
    } catch (e) {
      setAuctionRegs([]);
      toastOpenRef.current(e?.message || "Failed to load registrations", "error");
    } finally {
      setAregLoading(false);
    }
  }, [apiBaseUrl, orgReady, routes.auctionRegistrations, setAregLoading, setAregSelected, setAuctionRegs, setSyncInfo]);

  const onSelectAuction = useCallback(async (auctionRow, { viewMode = "stats" } = {}) => {
    setAuctionViewMode(viewMode === "registrations" ? "registrations" : "stats");
    setSelectedAuction(auctionRow || null);
    setAuctionSummary(null);
    setAuctionRegs([]);
    setAregSearch("");
    setAregShipperFilter("all");
    setAregNewUserFilter("all");
    setAregPickupFilter("all");
    setAregShippedFilter("all");
    setAregConsignorFilter("all");
    setAregPickupDraftByEmail({});
    setAregPickupSavingByEmail({});
    setAregPickupEditByEmail({});
    setAregShelfDraftByEmail({});
    setAregShelfSavingByEmail({});
    setAregAmountDueDraftByEmail({});
    setLotsAgg({});
    setAregSelected(new Set());
    setAuctionEndAtLocal("");
    setAuctionConsignors(getConsignorsFromAuction(auctionRow));
    setAuctionAccountManagerIds(getAuctionAccountManagerIds(auctionRow, accountManagers));
    setAuctionPickupLocations(getAuctionPickupLocationsFromAuction(auctionRow));
    setAuctionPickupSlots(buildAuctionPickupSlotDrafts(auctionRow));
    setAuctionLocation(String(auctionRow?.location || ""));
    if (auctionRow) {
      const auctionId = auctionRow.auctionId || auctionRow.id;
      await fetchAuctionSummary(auctionId, { force: false });
      await fetchAuctionRegsPage({ auctionId, sync: false, force: false });
    }
  }, [
    accountManagers,
    buildAuctionPickupSlotDrafts,
    fetchAuctionRegsPage,
    fetchAuctionSummary,
    getAuctionAccountManagerIds,
    getAuctionPickupLocationsFromAuction,
    getConsignorsFromAuction,
    setAregAmountDueDraftByEmail,
    setAregConsignorFilter,
    setAregNewUserFilter,
    setAregPickupDraftByEmail,
    setAregPickupEditByEmail,
    setAregPickupFilter,
    setAregPickupSavingByEmail,
    setAregSearch,
    setAregSelected,
    setAregShelfDraftByEmail,
    setAregShelfSavingByEmail,
    setAregShippedFilter,
    setAregShipperFilter,
    setAuctionAccountManagerIds,
    setAuctionConsignors,
    setAuctionEndAtLocal,
    setAuctionLocation,
    setAuctionPickupLocations,
    setAuctionPickupSlots,
    setAuctionRegs,
    setAuctionSummary,
    setAuctionViewMode,
    setLotsAgg,
    setSelectedAuction,
  ]);

  const openAuctionFromHibidView = useCallback(async (auctionRow) => {
    if (!auctionRow) return;
    await onSelectAuction(auctionRow, { viewMode: "stats" });
  }, [onSelectAuction]);

  const backToAuctionList = useCallback(() => {
    setSelectedAuction(null);
    setAuctionSummary(null);
    setAuctionRegs([]);
    setAregSearch("");
    setAregNewUserFilter("all");
    setAregShipperFilter("all");
    setAregPickupFilter("all");
    setAregShippedFilter("all");
    setAregConsignorFilter("all");
    setAregPickupDraftByEmail({});
    setAregPickupSavingByEmail({});
    setAregPickupEditByEmail({});
    setAregShelfDraftByEmail({});
    setAregShelfSavingByEmail({});
    setAregAmountDueDraftByEmail({});
    setAregSelected(new Set());
    setAuctionAccountManagerIds([]);
    setAuctionPickupLocations([]);
    setAuctionPickupSlots([]);
  }, [
    setAregAmountDueDraftByEmail,
    setAregConsignorFilter,
    setAregNewUserFilter,
    setAregPickupDraftByEmail,
    setAregPickupEditByEmail,
    setAregPickupFilter,
    setAregPickupSavingByEmail,
    setAregSearch,
    setAregSelected,
    setAregShelfDraftByEmail,
    setAregShelfSavingByEmail,
    setAregShippedFilter,
    setAregShipperFilter,
    setAuctionAccountManagerIds,
    setAuctionPickupLocations,
    setAuctionPickupSlots,
    setAuctionRegs,
    setAuctionSummary,
    setAuctionViewMode,
    setSelectedAuction,
  ]);

  useEffect(() => {
    if (!Array.isArray(auctionRegs) || auctionRegs.length === 0) return;
    const patch = {};
    for (const row of auctionRegs) {
      const emailLower = normalize(row?.bidderEmail || row?.email);
      if (!isEmail(emailLower)) continue;
      const lotsCount = safeNum(row?.lotsCount);
      const totalHighBid = safeNum(row?.totalHighBid);
      const uploadId = row?.lotsUploadId || row?.uploadId;
      const pickedUpLotsCount = toAbsInt(row?.pickedUpLotsCount || 0);
      const shippedLotsCount = toAbsInt(row?.shippedLotsCount || 0);
      if ((lotsCount > 0 || totalHighBid > 0 || pickedUpLotsCount > 0 || shippedLotsCount > 0) && !lotsAgg[emailLower]) {
        patch[emailLower] = {
          lotsCount,
          totalHighBid,
          pickedUpLotsCount,
          pickedUp: !!row?.pickedUp,
          pickupStatusLabel: String(row?.pickupStatusLabel || `${pickedUpLotsCount}/${toAbsInt(lotsCount)}`),
          shippedLotsCount,
          shipped: !!row?.shipped,
          shippedStatusLabel: String(row?.shippedStatusLabel || `${shippedLotsCount}/${toAbsInt(lotsCount)}`),
          uploadId,
        };
      }
    }
    if (Object.keys(patch).length) setLotsAgg((prev) => ({ ...prev, ...patch }));
  }, [auctionRegs, isEmail, lotsAgg, normalize, safeNum, setLotsAgg, toAbsInt]);

  const fetchUserLots = useCallback(async (emailLower, auctionId) => {
    const { res, data } = await apiFetchJsonRef.current(
      `${apiBaseUrl}${routes.userLots}?email=${encodeURIComponent(emailLower)}&auctionId=${encodeURIComponent(auctionId)}`
    );
    if (handleNeedsAuthRef.current(res, data)) throw new Error("Authorization required.");
    if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Request failed (${res.status})`);
    return data;
  }, [apiBaseUrl, routes.userLots]);

  const openLotsModal = useCallback(async (regRow) => {
    const auctionId = selectedAuctionId;
    const emailLower = normalize(regRow?.bidderEmail || regRow?.email);
    if (!auctionId || !isEmail(emailLower)) return;
    setLotsUser(regRow);
    setLotsOpen(true);
    setLotsBusy(true);
    setLotsErr("");
    setLotsData(null);
    try {
      const cached = lotsAgg[emailLower];
      if (cached?.lots && Array.isArray(cached.lots)) {
        setLotsData(cached);
        return;
      }
      const data = await fetchUserLots(emailLower, auctionId);
      setLotsData(data);
      setLotsAgg((prev) => ({
        ...prev,
        [emailLower]: {
          lotsCount: safeNum(data?.lotsCount),
          totalHighBid: safeNum(data?.totalHighBid),
          pickedUp: !!data?.pickedUp,
          pickedUpLotsCount: toAbsInt(data?.pickedUpLotsCount || 0),
          pickupStatusLabel: String(data?.pickupStatusLabel || ""),
          shipped: !!data?.shipped,
          shippedLotsCount: toAbsInt(data?.shippedLotsCount || 0),
          shippedStatusLabel: String(data?.shippedStatusLabel || ""),
          delivered: !!data?.delivered,
          deliveredLotsCount: toAbsInt(data?.deliveredLotsCount || 0),
          deliveredStatusLabel: String(data?.deliveredStatusLabel || ""),
          delivery: data?.delivery && typeof data.delivery === "object" ? data.delivery : prev?.[emailLower]?.delivery,
          uploadId: data?.uploadId,
          lots: Array.isArray(data?.lots) ? data.lots : [],
        },
      }));
    } catch (e) {
      setLotsErr(e?.message || "Failed to load lots");
    } finally {
      setLotsBusy(false);
    }
  }, [fetchUserLots, isEmail, lotsAgg, normalize, safeNum, selectedAuctionId, setLotsAgg, setLotsBusy, setLotsData, setLotsErr, setLotsOpen, setLotsUser, toAbsInt]);

  const updateRegistrationPickup = useCallback(async ({ auctionId, email, pickedUp }) => {
    if (!orgReady) return;
    const aId = String(auctionId || "").trim();
    const em = normalize(email);
    if (!aId || !isEmail(em)) return;
    try {
      const { res, data } = await apiFetchJsonRef.current(`${apiBaseUrl}${routes.pickupUpdate}`, {
        method: "POST",
        body: JSON.stringify({ auctionId: aId, email: em, pickedUp: !!pickedUp }),
      });
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Update failed (${res.status})`);

      setAuctionRegs((prev) =>
        (prev || []).map((row) => {
          const rowEmail = normalize(row?.bidderEmail || row?.email);
          if (rowEmail !== em) return row;
          return {
            ...row,
            pickedUp: !!data?.pickedUp,
            pickedUpLotsCount: toAbsInt(data?.pickedUpLotsCount || 0),
            pickupStatusLabel: String(data?.pickupStatusLabel || ""),
          };
        })
      );
      setLotsAgg((prev) => ({
        ...prev,
        [em]: {
          ...(prev?.[em] || {}),
          pickedUp: !!data?.pickedUp,
          pickedUpLotsCount: toAbsInt(data?.pickedUpLotsCount || 0),
          pickupStatusLabel: String(data?.pickupStatusLabel || ""),
          lots: Array.isArray(prev?.[em]?.lots)
            ? prev[em].lots.map((lot) => ({ ...lot, pickedUp: !!data?.pickedUp }))
            : prev?.[em]?.lots,
        },
      }));
      setLotsData((prev) => {
        if (!prev || normalize(lotsUser?.bidderEmail || lotsUser?.email) !== em) return prev;
        return {
          ...prev,
          pickedUp: !!data?.pickedUp,
          pickedUpLotsCount: toAbsInt(data?.pickedUpLotsCount || 0),
          pickupStatusLabel: String(data?.pickupStatusLabel || ""),
          lots: Array.isArray(prev?.lots) ? prev.lots.map((lot) => ({ ...lot, pickedUp: !!data?.pickedUp })) : prev?.lots,
        };
      });
      if (data?.pickupThankYouEmailSent) {
        toastOpenRef.current("Pickup saved and thank-you email sent.", "success");
      } else if (data?.pickupThankYouEmailWarning) {
        toastOpenRef.current(data.pickupThankYouEmailWarning, "info");
      }
    } catch (e) {
      toastOpenRef.current(e?.message || "Failed to update pickup", "error");
    }
  }, [apiBaseUrl, isEmail, lotsUser, normalize, orgReady, routes.pickupUpdate, setAuctionRegs, setLotsAgg, setLotsData, toAbsInt]);

  const updateRegistrationShipped = useCallback(async ({ auctionId, email, shipped }) => {
    if (!orgReady) return;
    const aId = String(auctionId || "").trim();
    const em = normalize(email);
    if (!aId || !isEmail(em)) return;
    try {
      const { res, data } = await apiFetchJsonRef.current(`${apiBaseUrl}${routes.shippedUpdate}`, {
        method: "POST",
        body: JSON.stringify({ auctionId: aId, email: em, shipped: !!shipped }),
      });
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Update failed (${res.status})`);

      setAuctionRegs((prev) =>
        (prev || []).map((row) => {
          const rowEmail = normalize(row?.bidderEmail || row?.email);
          if (rowEmail !== em) return row;
          return {
            ...row,
            shipped: !!data?.shipped,
            shippedLotsCount: toAbsInt(data?.shippedLotsCount || 0),
            shippedStatusLabel: String(data?.shippedStatusLabel || ""),
          };
        })
      );
      setLotsAgg((prev) => ({
        ...prev,
        [em]: {
          ...(prev?.[em] || {}),
          shipped: !!data?.shipped,
          shippedLotsCount: toAbsInt(data?.shippedLotsCount || 0),
          shippedStatusLabel: String(data?.shippedStatusLabel || ""),
          lots: Array.isArray(prev?.[em]?.lots)
            ? prev[em].lots.map((lot) => ({ ...lot, shipped: !!data?.shipped }))
            : prev?.[em]?.lots,
        },
      }));
      setLotsData((prev) => {
        if (!prev || normalize(lotsUser?.bidderEmail || lotsUser?.email) !== em) return prev;
        return {
          ...prev,
          shipped: !!data?.shipped,
          shippedLotsCount: toAbsInt(data?.shippedLotsCount || 0),
          shippedStatusLabel: String(data?.shippedStatusLabel || ""),
          lots: Array.isArray(prev?.lots) ? prev.lots.map((lot) => ({ ...lot, shipped: !!data?.shipped })) : prev?.lots,
        };
      });
      if (data?.shippedThankYouEmailSent) {
        toastOpenRef.current("Shipped saved and thank-you email sent.", "success");
      } else if (data?.shippedThankYouEmailWarning) {
        toastOpenRef.current(data.shippedThankYouEmailWarning, "info");
      }
    } catch (e) {
      toastOpenRef.current(e?.message || "Failed to update shipped", "error");
    }
  }, [apiBaseUrl, isEmail, lotsUser, normalize, orgReady, routes.shippedUpdate, setAuctionRegs, setLotsAgg, setLotsData, toAbsInt]);

  const updateRegistrationDelivered = useCallback(async ({ auctionId, email, delivered, status = "", failedReason = "" }) => {
    if (!orgReady) return;
    const aId = String(auctionId || "").trim();
    const em = normalize(email);
    if (!aId || !isEmail(em)) return;
    const normalizedStatus = String(status || "").trim().toLowerCase();
    try {
      const { res, data } = await apiFetchJsonRef.current(`${apiBaseUrl}${routes.deliveredUpdate}`, {
        method: "POST",
        body: JSON.stringify({
          auctionId: aId,
          email: em,
          delivered: !!delivered,
          ...(normalizedStatus ? { status: normalizedStatus } : {}),
          ...(failedReason ? { failedReason: String(failedReason || "") } : {}),
        }),
      });
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Update failed (${res.status})`);

      setAuctionRegs((prev) =>
        (prev || []).map((row) => {
          const rowEmail = normalize(row?.bidderEmail || row?.email);
          if (rowEmail !== em) return row;
          return {
            ...row,
            delivered: !!data?.delivered,
            deliveredLotsCount: toAbsInt(data?.deliveredLotsCount || 0),
            deliveredStatusLabel: String(data?.deliveredStatusLabel || ""),
            delivery: data?.delivery && typeof data.delivery === "object"
              ? { ...(row?.delivery || {}), ...data.delivery }
              : row?.delivery,
            deliveryDelivered: !!data?.delivered,
            deliveryDeliveredAtMs: data?.delivery?.deliveredAtMs || row?.deliveryDeliveredAtMs || 0,
            deliveryFailed: !!data?.delivery?.failed,
            deliveryFailedReason: String(data?.delivery?.failedReason || row?.deliveryFailedReason || ""),
            deliveryFailedAtMs: data?.delivery?.failedAtMs || row?.deliveryFailedAtMs || 0,
          };
        })
      );
      setLotsAgg((prev) => ({
        ...prev,
        [em]: {
          ...(prev?.[em] || {}),
          delivered: !!data?.delivered,
          deliveredLotsCount: toAbsInt(data?.deliveredLotsCount || 0),
          deliveredStatusLabel: String(data?.deliveredStatusLabel || ""),
          delivery: data?.delivery && typeof data.delivery === "object"
            ? { ...(prev?.[em]?.delivery || {}), ...data.delivery }
            : prev?.[em]?.delivery,
          lots: Array.isArray(prev?.[em]?.lots)
            ? prev[em].lots.map((lot) => ({ ...lot, delivered: !!data?.delivered }))
            : prev?.[em]?.lots,
        },
      }));
      setLotsData((prev) => {
        if (!prev || normalize(lotsUser?.bidderEmail || lotsUser?.email) !== em) return prev;
        return {
          ...prev,
          delivered: !!data?.delivered,
          deliveredLotsCount: toAbsInt(data?.deliveredLotsCount || 0),
          deliveredStatusLabel: String(data?.deliveredStatusLabel || ""),
          delivery: data?.delivery && typeof data.delivery === "object"
            ? { ...(prev?.delivery || {}), ...data.delivery }
            : prev?.delivery,
          lots: Array.isArray(prev?.lots) ? prev.lots.map((lot) => ({ ...lot, delivered: !!data?.delivered })) : prev?.lots,
        };
      });
      if (data?.deliveredThankYouEmailSent) {
        toastOpenRef.current("Delivered saved and thank-you email sent.", "success");
      } else if (normalizedStatus === "failed") {
        toastOpenRef.current("Failed delivery saved.", "success");
      } else if (data?.deliveredThankYouEmailWarning) {
        toastOpenRef.current(data.deliveredThankYouEmailWarning, "info");
      }
    } catch (e) {
      toastOpenRef.current(e?.message || "Failed to update delivered", "error");
    }
  }, [apiBaseUrl, isEmail, lotsUser, normalize, orgReady, routes.deliveredUpdate, setAuctionRegs, setLotsAgg, setLotsData, toAbsInt]);

  const uploadRegistrationDeliveryProofPhotos = useCallback(async ({ auctionId, email, files = [] }) => {
    if (!orgReady) return null;
    const aId = String(auctionId || "").trim();
    const em = normalize(email);
    const uploadFiles = Array.from(files || []).filter(Boolean);
    if (!aId || !isEmail(em) || !uploadFiles.length) return null;
    try {
      const fd = new FormData();
      fd.append("auctionId", aId);
      fd.append("email", em);
      uploadFiles.forEach((file) => {
        fd.append("files", file, file?.name || "delivery-proof.jpg");
      });
      const { res, data } = await apiFetchJsonRef.current(`${apiBaseUrl}${routes.deliveryPhotosUpload}`, {
        method: "POST",
        body: fd,
      });
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Upload failed (${res.status})`);

      setAuctionRegs((prev) =>
        (prev || []).map((row) => {
          const rowEmail = normalize(row?.bidderEmail || row?.email);
          if (rowEmail !== em) return row;
          return {
            ...row,
            delivery: data?.delivery && typeof data.delivery === "object"
              ? { ...(row?.delivery || {}), ...data.delivery }
              : row?.delivery,
            deliveryProofPhotos: Array.isArray(data?.proofPhotos) ? data.proofPhotos : row?.deliveryProofPhotos,
          };
        })
      );
      setLotsAgg((prev) => ({
        ...prev,
        [em]: {
          ...(prev?.[em] || {}),
          delivery: data?.delivery && typeof data.delivery === "object"
            ? { ...(prev?.[em]?.delivery || {}), ...data.delivery }
            : prev?.[em]?.delivery,
        },
      }));
      setLotsData((prev) => {
        if (!prev || normalize(lotsUser?.bidderEmail || lotsUser?.email) !== em) return prev;
        return {
          ...prev,
          delivery: data?.delivery && typeof data.delivery === "object"
            ? { ...(prev?.delivery || {}), ...data.delivery }
            : prev?.delivery,
        };
      });
      const uploadedCount = toAbsInt(data?.uploadedCount || uploadFiles.length);
      toastOpenRef.current(`Uploaded ${uploadedCount} delivery photo${uploadedCount === 1 ? "" : "s"}.`, "success");
      return data?.delivery || null;
    } catch (e) {
      toastOpenRef.current(e?.message || "Failed to upload delivery photos", "error");
      return null;
    }
  }, [apiBaseUrl, isEmail, lotsUser, normalize, orgReady, routes.deliveryPhotosUpload, setAuctionRegs, setLotsAgg, setLotsData, toAbsInt]);

  const closeRegistrationFulfillmentConfirm = useCallback(() => {
    if (registrationFulfillmentConfirmBusy) return;
    setRegistrationFulfillmentConfirm({ open: false, type: "", nextValue: false, row: null, userName: "" });
  }, [registrationFulfillmentConfirmBusy, setRegistrationFulfillmentConfirm]);

  const openRegistrationFulfillmentConfirm = useCallback(({ type = "", row = null, nextValue = false } = {}) => {
    const normalizedType = String(type || "").trim().toLowerCase();
    if (!["pickup", "shipped", "delivery"].includes(normalizedType) || !row) return;
    const userName =
      String(getDisplayBidderName(row) || getDisplayUsername(row) || row?.bidderEmail || row?.email || "").trim() || "this user";
    setRegistrationFulfillmentConfirm({
      open: true,
      type: normalizedType,
      nextValue: !!nextValue,
      row,
      userName,
    });
  }, [getDisplayBidderName, getDisplayUsername, setRegistrationFulfillmentConfirm]);

  const confirmRegistrationFulfillmentChange = useCallback(async () => {
    if (registrationFulfillmentConfirmBusy) return;
    const { type, nextValue, row } = registrationFulfillmentConfirm || {};
    if (!row || !type) return;
    setRegistrationFulfillmentConfirmBusy(true);
    try {
      if (type === "pickup") {
        await updateRegistrationPickup({
          auctionId: selectedAuctionId,
          email: row?.bidderEmail || row?.email,
          pickedUp: !!nextValue,
        });
      } else if (type === "shipped") {
        await updateRegistrationShipped({
          auctionId: selectedAuctionId,
          email: row?.bidderEmail || row?.email,
          shipped: !!nextValue,
        });
      } else if (type === "delivery") {
        await updateRegistrationDelivered({
          auctionId: selectedAuctionId,
          email: row?.bidderEmail || row?.email,
          delivered: !!nextValue,
        });
      }
      setRegistrationFulfillmentConfirm({ open: false, type: "", nextValue: false, row: null, userName: "" });
    } finally {
      setRegistrationFulfillmentConfirmBusy(false);
    }
  }, [registrationFulfillmentConfirm, registrationFulfillmentConfirmBusy, selectedAuctionId, setRegistrationFulfillmentConfirm, setRegistrationFulfillmentConfirmBusy, updateRegistrationDelivered, updateRegistrationPickup, updateRegistrationShipped]);

  const updateLotPickup = useCallback(async ({ lotId, email, pickedUp }) => {
    if (!orgReady) return;
    const lid = String(lotId || "").trim();
    const em = normalize(email);
    if (!lid || !isEmail(em)) return;
    try {
      const { res, data } = await apiFetchJsonRef.current(`${apiBaseUrl}${routes.lotPickupUpdate}`, {
        method: "POST",
        body: JSON.stringify({ lotId: lid, pickedUp: !!pickedUp }),
      });
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Update failed (${res.status})`);

      setLotsData((prev) => {
        if (!prev) return prev;
        const totalLotsCount = toAbsInt(data?.totalLotsCount || prev?.lotsCount || 0);
        const pickedUpLotsCount = toAbsInt(data?.pickedUpLotsCount || 0);
        return {
          ...prev,
          pickedUp: totalLotsCount > 0 && pickedUpLotsCount >= totalLotsCount,
          pickedUpLotsCount,
          pickupStatusLabel: String(data?.pickupStatusLabel || ""),
          lots: Array.isArray(prev?.lots)
            ? prev.lots.map((lot) => (String(lot?.id || "") === lid ? { ...lot, pickedUp: !!pickedUp } : lot))
            : prev?.lots,
        };
      });
      setLotsAgg((prev) => ({
        ...prev,
        [em]: {
          ...(prev?.[em] || {}),
          pickedUp:
            toAbsInt(data?.pickedUpLotsCount || 0) >= toAbsInt(data?.totalLotsCount || prev?.[em]?.lotsCount || 0) &&
            toAbsInt(data?.totalLotsCount || prev?.[em]?.lotsCount || 0) > 0,
          pickedUpLotsCount: toAbsInt(data?.pickedUpLotsCount || 0),
          pickupStatusLabel: String(data?.pickupStatusLabel || ""),
          lots: Array.isArray(prev?.[em]?.lots)
            ? prev[em].lots.map((lot) => (String(lot?.id || "") === lid ? { ...lot, pickedUp: !!pickedUp } : lot))
            : prev?.[em]?.lots,
        },
      }));
      setAuctionRegs((prev) =>
        (prev || []).map((row) => {
          const rowEmail = normalize(row?.bidderEmail || row?.email);
          if (rowEmail !== em) return row;
          return {
            ...row,
            pickedUp:
              toAbsInt(data?.pickedUpLotsCount || 0) >= toAbsInt(data?.totalLotsCount || row?.lotsCount || 0) &&
              toAbsInt(data?.totalLotsCount || row?.lotsCount || 0) > 0,
            pickedUpLotsCount: toAbsInt(data?.pickedUpLotsCount || 0),
            pickupStatusLabel: String(data?.pickupStatusLabel || ""),
          };
        })
      );
    } catch (e) {
      toastOpenRef.current(e?.message || "Failed to update lot pickup", "error");
    }
  }, [apiBaseUrl, isEmail, normalize, orgReady, routes.lotPickupUpdate, setAuctionRegs, setLotsAgg, setLotsData, toAbsInt]);

  const updateLotShipped = useCallback(async ({ lotId, email, shipped }) => {
    if (!orgReady) return;
    const lid = String(lotId || "").trim();
    const em = normalize(email);
    if (!lid || !isEmail(em)) return;
    try {
      const { res, data } = await apiFetchJsonRef.current(`${apiBaseUrl}${routes.lotShippedUpdate}`, {
        method: "POST",
        body: JSON.stringify({ lotId: lid, shipped: !!shipped }),
      });
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Update failed (${res.status})`);

      setLotsData((prev) => {
        if (!prev) return prev;
        const totalLotsCount = toAbsInt(data?.totalLotsCount || prev?.lotsCount || 0);
        const shippedLotsCount = toAbsInt(data?.shippedLotsCount || 0);
        return {
          ...prev,
          shipped: totalLotsCount > 0 && shippedLotsCount >= totalLotsCount,
          shippedLotsCount,
          shippedStatusLabel: String(data?.shippedStatusLabel || ""),
          lots: Array.isArray(prev?.lots)
            ? prev.lots.map((lot) => (String(lot?.id || "") === lid ? { ...lot, shipped: !!shipped } : lot))
            : prev?.lots,
        };
      });
      setLotsAgg((prev) => ({
        ...prev,
        [em]: {
          ...(prev?.[em] || {}),
          shipped:
            toAbsInt(data?.shippedLotsCount || 0) >= toAbsInt(data?.totalLotsCount || prev?.[em]?.lotsCount || 0) &&
            toAbsInt(data?.totalLotsCount || prev?.[em]?.lotsCount || 0) > 0,
          shippedLotsCount: toAbsInt(data?.shippedLotsCount || 0),
          shippedStatusLabel: String(data?.shippedStatusLabel || ""),
          lots: Array.isArray(prev?.[em]?.lots)
            ? prev[em].lots.map((lot) => (String(lot?.id || "") === lid ? { ...lot, shipped: !!shipped } : lot))
            : prev?.[em]?.lots,
        },
      }));
      setAuctionRegs((prev) =>
        (prev || []).map((row) => {
          const rowEmail = normalize(row?.bidderEmail || row?.email);
          if (rowEmail !== em) return row;
          return {
            ...row,
            shipped:
              toAbsInt(data?.shippedLotsCount || 0) >= toAbsInt(data?.totalLotsCount || row?.lotsCount || 0) &&
              toAbsInt(data?.totalLotsCount || row?.lotsCount || 0) > 0,
            shippedLotsCount: toAbsInt(data?.shippedLotsCount || 0),
            shippedStatusLabel: String(data?.shippedStatusLabel || ""),
          };
        })
      );
    } catch (e) {
      toastOpenRef.current(e?.message || "Failed to update lot shipped", "error");
    }
  }, [apiBaseUrl, isEmail, normalize, orgReady, routes.lotShippedUpdate, setAuctionRegs, setLotsAgg, setLotsData, toAbsInt]);

  const updateLotDelivered = useCallback(async ({ lotId, email, delivered }) => {
    if (!orgReady) return;
    const lid = String(lotId || "").trim();
    const em = normalize(email);
    if (!lid || !isEmail(em)) return;
    try {
      const { res, data } = await apiFetchJsonRef.current(`${apiBaseUrl}${routes.lotDeliveredUpdate}`, {
        method: "POST",
        body: JSON.stringify({ lotId: lid, delivered: !!delivered }),
      });
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Update failed (${res.status})`);

      setLotsData((prev) => {
        if (!prev) return prev;
        const totalLotsCount = toAbsInt(data?.totalLotsCount || prev?.lotsCount || 0);
        const deliveredLotsCount = toAbsInt(data?.deliveredLotsCount || 0);
        return {
          ...prev,
          delivered: totalLotsCount > 0 && deliveredLotsCount >= totalLotsCount,
          deliveredLotsCount,
          deliveredStatusLabel: String(data?.deliveredStatusLabel || ""),
          lots: Array.isArray(prev?.lots)
            ? prev.lots.map((lot) => (String(lot?.id || "") === lid ? { ...lot, delivered: !!delivered } : lot))
            : prev?.lots,
        };
      });
      setLotsAgg((prev) => ({
        ...prev,
        [em]: {
          ...(prev?.[em] || {}),
          delivered:
            toAbsInt(data?.deliveredLotsCount || 0) >= toAbsInt(data?.totalLotsCount || prev?.[em]?.lotsCount || 0) &&
            toAbsInt(data?.totalLotsCount || prev?.[em]?.lotsCount || 0) > 0,
          deliveredLotsCount: toAbsInt(data?.deliveredLotsCount || 0),
          deliveredStatusLabel: String(data?.deliveredStatusLabel || ""),
          lots: Array.isArray(prev?.[em]?.lots)
            ? prev[em].lots.map((lot) => (String(lot?.id || "") === lid ? { ...lot, delivered: !!delivered } : lot))
            : prev?.[em]?.lots,
        },
      }));
      setAuctionRegs((prev) =>
        (prev || []).map((row) => {
          const rowEmail = normalize(row?.bidderEmail || row?.email);
          if (rowEmail !== em) return row;
          return {
            ...row,
            delivered:
              toAbsInt(data?.deliveredLotsCount || 0) >= toAbsInt(data?.totalLotsCount || row?.lotsCount || 0) &&
              toAbsInt(data?.totalLotsCount || row?.lotsCount || 0) > 0,
            deliveredLotsCount: toAbsInt(data?.deliveredLotsCount || 0),
            deliveredStatusLabel: String(data?.deliveredStatusLabel || ""),
            delivery: {
              ...(row?.delivery || {}),
              delivered:
                toAbsInt(data?.deliveredLotsCount || 0) >= toAbsInt(data?.totalLotsCount || row?.lotsCount || 0) &&
                toAbsInt(data?.totalLotsCount || row?.lotsCount || 0) > 0,
              deliveredLotsCount: toAbsInt(data?.deliveredLotsCount || 0),
              deliveredStatusLabel: String(data?.deliveredStatusLabel || ""),
            },
          };
        })
      );
    } catch (e) {
      toastOpenRef.current(e?.message || "Failed to update delivered", "error");
    }
  }, [apiBaseUrl, isEmail, normalize, orgReady, routes.lotDeliveredUpdate, setAuctionRegs, setLotsAgg, setLotsData, toAbsInt]);

  const resolveFileNameMismatchDialog = useCallback((proceed) => {
    const resolver = fileNameMismatchResolveRef.current;
    fileNameMismatchResolveRef.current = null;
    setFileNameMismatchDialog({ open: false, uploadKindLabel: "file", auctionName: "", fileName: "" });
    if (typeof resolver === "function") resolver(!!proceed);
  }, [fileNameMismatchResolveRef, setFileNameMismatchDialog]);

  const openFileNameMismatchDialog = useCallback(({ fileName = "", uploadKindLabel = "file", auctionName = "" } = {}) =>
    new Promise((resolve) => {
      fileNameMismatchResolveRef.current = resolve;
      setFileNameMismatchDialog({
        open: true,
        uploadKindLabel: String(uploadKindLabel || "file"),
        fileName: String(fileName || ""),
        auctionName: String(auctionName || ""),
      });
    }), [fileNameMismatchResolveRef, setFileNameMismatchDialog]);

  const confirmAuctionFileNameMatch = useCallback(async (file, uploadKindLabel = "file", auctionNameOverride = "") => {
    const fileName = String(file?.name || "").trim();
    const auctionName = String(auctionNameOverride || selectedAuction?.auctionName || selectedAuctionLabel || "").trim();
    if (!fileName || !auctionName) return true;

    const normalizeForFileMatch = (value) =>
      String(value || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "");

    const normalizedFile = normalizeForFileMatch(fileName);
    const normalizedAuction = normalizeForFileMatch(auctionName);
    const nameMatches = !!normalizedAuction && normalizedFile.includes(normalizedAuction);
    if (nameMatches) return true;
    return openFileNameMismatchDialog({ fileName, uploadKindLabel, auctionName });
  }, [openFileNameMismatchDialog, selectedAuction, selectedAuctionLabel]);

  const uploadLotsCsvForAuction = useCallback(async ({
    file,
    auctionId,
    auctionName = "",
    auctionEndAtMs = null,
    consignors = [],
    location = "",
    requireNameMatch = true,
    refreshSelectedAuction = true,
  } = {}) => {
    if (!orgReady) return null;
    const aId = String(auctionId || "").trim();
    if (!aId || !file) return null;
    if (requireNameMatch && !(await confirmAuctionFileNameMatch(file, "lot stats file", auctionName))) {
      throw new Error("Lot stats upload canceled.");
    }

    const normalizedConsignors = uniqSortedOptions(consignors || []);
    const primaryConsignor = String(normalizedConsignors[0] || "").trim();
    const locationSafe = String(location || "").trim();
    setUploadDialogText("Uploading lot stats file. Please wait…");
    setUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      const qs = new URLSearchParams();
      qs.set("auctionId", aId);
      if (Number.isFinite(Number(auctionEndAtMs)) && Number(auctionEndAtMs) > 0) {
        qs.set("auctionEndAtMs", String(Math.floor(Number(auctionEndAtMs))));
      }
      if (primaryConsignor) qs.set("consignor", primaryConsignor);
      if (normalizedConsignors.length) qs.set("consignors", normalizedConsignors.join("|"));
      if (locationSafe) qs.set("location", locationSafe);
      const { res, data } = await apiFetchJsonRef.current(`${apiBaseUrl}${routes.lotsUpload}?${qs.toString()}`, {
        method: "POST",
        body: fd,
      });
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Upload failed (${res.status})`);
      setAuctionFieldOptions((prev) => mergeAuctionFieldOptions(prev, { consignors: normalizedConsignors, locations: locationSafe ? [locationSafe] : [] }));
      invalidateUsersCacheRef.current();
      const rows = await fetchAuctions({ force: true, sync: false });
      void rows;
      if (refreshSelectedAuction && aId === selectedAuctionId) {
        await fetchAuctionSummary(aId, { force: true });
        await fetchAuctionRegsPage({ auctionId: aId, sync: false, force: true });
      }
      return data;
    } finally {
      setUploadBusy(false);
      setUploadDialogText("");
    }
  }, [
    apiBaseUrl,
    confirmAuctionFileNameMatch,
    fetchAuctionRegsPage,
    fetchAuctionSummary,
    fetchAuctions,
    mergeAuctionFieldOptions,
    orgReady,
    routes.lotsUpload,
    selectedAuctionId,
    setAuctionFieldOptions,
    setUploadBusy,
    setUploadDialogText,
    uniqSortedOptions,
  ]);

  const uploadShippingXlsx = useCallback(async (file, { replaceExisting = false } = {}) => {
    if (!orgReady) return;
    const auctionId = selectedAuctionId;
    if (!auctionId || !file) return;
    if (!isXlsxFile(file)) {
      toastOpenRef.current("Upload an .xlsx file.", "error");
      return;
    }
    if (!(await confirmAuctionFileNameMatch(file, "shipping file"))) {
      toastOpenRef.current("Shipping upload canceled.", "info");
      return;
    }
    setUploadDialogText(replaceExisting ? "Replacing shipping file. Please wait…" : "Uploading shipping file. Please wait…");
    setShipBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      const qs = new URLSearchParams();
      qs.set("auctionId", auctionId);
      if (replaceExisting) qs.set("replaceExisting", "true");
      const { res, data } = await apiFetchJsonRef.current(`${apiBaseUrl}${routes.shippingUpload}?${qs.toString()}`, {
        method: "POST",
        body: fd,
      });
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Upload failed (${res.status})`);
      const updated = formatNumber2(toAbsInt(data?.updatedRegistrations ?? data?.updated ?? 0));
      const deleted = formatNumber2(toAbsInt(data?.deletedExistingRegistrations ?? 0));
      if (replaceExisting) {
        toastOpenRef.current(`Shipping replaced: ${updated} registrations (removed ${deleted}).`, "success");
      } else {
        toastOpenRef.current(`Shipping updated: ${updated}`, "success");
      }
      await fetchAuctionRegsPage({ auctionId, sync: false, force: true });
      await fetchAuctionSummary(auctionId, { force: true });
      await fetchAuctions({ force: true, sync: false });
    } catch (e) {
      toastOpenRef.current(e?.message || "Shipping upload failed", "error");
    } finally {
      setShipBusy(false);
      setUploadDialogText("");
    }
  }, [
    apiBaseUrl,
    confirmAuctionFileNameMatch,
    fetchAuctionRegsPage,
    fetchAuctionSummary,
    fetchAuctions,
    formatNumber2,
    isXlsxFile,
    orgReady,
    routes.shippingUpload,
    selectedAuctionId,
    setShipBusy,
    setUploadDialogText,
    toAbsInt,
  ]);

  const requestShippingUploadFile = useCallback(() => {
    shippingFileInputRef.current?.click();
  }, [shippingFileInputRef]);

  const uploadLotsCsv = useCallback(async (file) => {
    if (!orgReady) return;
    const auctionId = selectedAuctionId;
    if (!auctionId || !file) return;
    const consignors = uniqSortedOptions([]);
    const location = String(createAuctionLocation || "").trim();
    const endMs = fromInputDateTimeLocalToMs(createAuctionEndAtLocal);
    try {
      const data = await uploadLotsCsvForAuction({
        file,
        auctionId,
        auctionName: selectedAuctionLabel,
        auctionEndAtMs: endMs,
        consignors,
        location,
        requireNameMatch: true,
        refreshSelectedAuction: true,
      });
      if (!data) return;
      toastOpenRef.current(
        `Lots uploaded: ${formatNumber2(toAbsInt(data?.totalLots ?? data?.lotsTotalCount ?? 0))} • Unique bidders: ${formatNumber2(
          toAbsInt(data?.uniqueBidders ?? data?.lotsUniqueBidders ?? 0)
        )}`,
        "success"
      );
    } catch (e) {
      if (String(e?.message || "").toLowerCase().includes("canceled")) {
        toastOpenRef.current("Lot stats upload canceled.", "info");
      } else {
        toastOpenRef.current(e?.message || "Lots upload failed", "error");
      }
    }
  }, [createAuctionEndAtLocal, createAuctionLocation, formatNumber2, fromInputDateTimeLocalToMs, orgReady, selectedAuctionId, selectedAuctionLabel, toAbsInt, uniqSortedOptions, uploadLotsCsvForAuction]);

  const uploadListernautFile = useCallback(async (file) => {
    if (!orgReady) return;
    const auctionId = selectedAuctionId;
    if (!auctionId || !file) return;
    if (!(await confirmAuctionFileNameMatch(file, "Listernaut file", selectedAuctionLabel))) {
      toastOpenRef.current("Listernaut upload canceled.", "info");
      return;
    }
    setUploadDialogText("Uploading Listernaut file. Please wait…");
    setListernautUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      const qs = new URLSearchParams();
      qs.set("auctionId", auctionId);
      const { res, data } = await apiFetchJsonRef.current(`${apiBaseUrl}${routes.listernautUpload}?${qs.toString()}`, {
        method: "POST",
        body: fd,
      });
      if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Upload failed (${res.status})`);
      setLotsAgg({});
      await fetchAuctions({ force: true, sync: false });
      await fetchAuctionSummary(auctionId, { force: true });
      await fetchAuctionRegsPage({ auctionId, sync: false, force: true });
      toastOpenRef.current(
        `Listernaut mapped ${formatNumber2(toAbsInt(data?.updatedLots ?? data?.matchedLots ?? 0))} lots.`,
        "success"
      );
    } catch (e) {
      if (String(e?.message || "").toLowerCase().includes("canceled")) {
        toastOpenRef.current("Listernaut upload canceled.", "info");
      } else {
        toastOpenRef.current(e?.message || "Listernaut upload failed", "error");
      }
    } finally {
      setListernautUploadBusy(false);
      setUploadDialogText("");
    }
  }, [
    apiBaseUrl,
    confirmAuctionFileNameMatch,
    fetchAuctionRegsPage,
    fetchAuctionSummary,
    fetchAuctions,
    formatNumber2,
    orgReady,
    routes.listernautUpload,
    selectedAuctionId,
    selectedAuctionLabel,
    setListernautUploadBusy,
    setLotsAgg,
    setUploadDialogText,
    toAbsInt,
  ]);

  const requestListernautUploadFile = useCallback(() => {
    listernautFileInputRef.current?.click();
  }, [listernautFileInputRef]);

  const openCreateAuctionDialog = useCallback(() => {
    setCreateAuctionName("");
    setCreateAuctionEndAtLocal("");
    setCreateAuctionLocation("");
    setCreateAuctionLocationInput("");
    setCreateAuctionLocationCityOptions([]);
    setCreateAuctionRegistrationFile(null);
    setCreateAuctionLotsFile(null);
    setCreateAuctionOpen(true);
  }, [
    setCreateAuctionEndAtLocal,
    setCreateAuctionLocation,
    setCreateAuctionLocationCityOptions,
    setCreateAuctionLocationInput,
    setCreateAuctionLotsFile,
    setCreateAuctionName,
    setCreateAuctionOpen,
    setCreateAuctionRegistrationFile,
  ]);

  const createAuctionNow = useCallback(async () => {
    if (!orgReady || createAuctionBusy) return;
    const auctionName = String(createAuctionName || "").trim();
    if (!auctionName) {
      toastOpenRef.current("Auction name is required.", "error");
      return;
    }

    if (createAuctionRegistrationFile) {
      const ok = await confirmAuctionFileNameMatch(createAuctionRegistrationFile, "registration file", auctionName);
      if (!ok) {
        toastOpenRef.current("Registration upload canceled.", "info");
        return;
      }
    }

    if (createAuctionLotsFile) {
      const ok = await confirmAuctionFileNameMatch(createAuctionLotsFile, "lot stats file", auctionName);
      if (!ok) {
        toastOpenRef.current("Lot stats upload canceled.", "info");
        return;
      }
    }

    setCreateAuctionBusy(true);
    try {
      const createBody = { auctionName };
      const endMs = fromInputDateTimeLocalToMs(createAuctionEndAtLocal);
      if (endMs) createBody.auctionEndAtMs = endMs;
      const consignor = String(createAuctionConsignor || "").trim();
      if (consignor) {
        createBody.consignor = consignor;
        createBody.consignors = consignor;
      }
      const location = String(createAuctionLocation || "").trim();
      if (location) createBody.location = location;

      const { res: createRes, data: createData } = await apiFetchJsonRef.current(`${apiBaseUrl}${routes.auctionsCreate}`, {
        method: "POST",
        body: JSON.stringify(createBody),
      });
      if (!createRes.ok || createData?.ok !== true) {
        throw new Error(createData?.error || `Create failed (${createRes.status})`);
      }

      const auctionId = String(createData?.auctionId || "").trim();
      if (!auctionId) throw new Error("Create auction did not return auctionId.");
      const createdAuctionName = String(createData?.auctionName || auctionName).trim();

      if (createAuctionRegistrationFile) {
        const registrationFileIsXlsx = isXlsxFile(createAuctionRegistrationFile);
        setUploadDialogText(
          registrationFileIsXlsx
            ? "Uploading registration and shipping file. Please wait…"
            : "Uploading registration file. Please wait…"
        );
        setUploadBusy(true);
        try {
          const fd = new FormData();
          fd.append("file", createAuctionRegistrationFile, createAuctionRegistrationFile.name);
          const qs = new URLSearchParams();
          qs.set("auctionId", auctionId);
          qs.set("auctionName", createdAuctionName);
          const { res, data } = await apiFetchJsonRef.current(`${apiBaseUrl}${routes.auctionRegistrationsUpload}?${qs.toString()}`, {
            method: "POST",
            body: fd,
          });
          if (!res.ok || data?.ok !== true) throw new Error(data?.error || `Registration upload failed (${res.status})`);

          if (registrationFileIsXlsx) {
            const shippingFd = new FormData();
            shippingFd.append("file", createAuctionRegistrationFile, createAuctionRegistrationFile.name);
            const shippingQs = new URLSearchParams();
            shippingQs.set("auctionId", auctionId);
            const { res: shipRes, data: shipData } = await apiFetchJsonRef.current(
              `${apiBaseUrl}${routes.shippingUpload}?${shippingQs.toString()}`,
              {
                method: "POST",
                body: shippingFd,
              }
            );
            if (!shipRes.ok || shipData?.ok !== true) {
              throw new Error(shipData?.error || `Shipping upload failed (${shipRes.status})`);
            }
          }
        } finally {
          setUploadBusy(false);
          setUploadDialogText("");
        }
      }

      if (createAuctionLotsFile) {
        const lotsData = await uploadLotsCsvForAuction({
          file: createAuctionLotsFile,
          auctionId,
          auctionName: createdAuctionName,
          auctionEndAtMs: endMs,
          consignors: consignor ? [consignor] : [],
          location,
          requireNameMatch: false,
          refreshSelectedAuction: false,
        });
        if (lotsData?.ok !== true) {
          throw new Error("Lot stats upload failed.");
        }
      }

      const rows = await fetchAuctions({ force: true, sync: false });
      const createdAuctionRow = (Array.isArray(rows) ? rows : []).find(
        (row) => String(row?.auctionId || row?.id || "").trim() === auctionId
      );
      if (createdAuctionRow) {
        await onSelectAuction(createdAuctionRow);
      }

      setCreateAuctionOpen(false);
      toastOpenRef.current("Auction created successfully.", "success");
    } catch (e) {
      const message = String(e?.message || "").trim();
      if (message.toLowerCase().includes("canceled")) {
        toastOpenRef.current(message || "Upload canceled.", "info");
      } else {
        toastOpenRef.current(message || "Failed to create auction", "error");
      }
    } finally {
      setCreateAuctionBusy(false);
    }
  }, [
    apiBaseUrl,
    confirmAuctionFileNameMatch,
    createAuctionBusy,
    createAuctionConsignor,
    createAuctionEndAtLocal,
    createAuctionLocation,
    createAuctionLotsFile,
    createAuctionName,
    createAuctionRegistrationFile,
    fetchAuctions,
    fromInputDateTimeLocalToMs,
    isXlsxFile,
    onSelectAuction,
    orgReady,
    routes.auctionRegistrationsUpload,
    routes.auctionsCreate,
    routes.shippingUpload,
    setCreateAuctionBusy,
    setCreateAuctionOpen,
    setUploadBusy,
    setUploadDialogText,
    uploadLotsCsvForAuction,
  ]);

  return {
    fetchAuctions,
    fetchAuctionSummary,
    fetchAuctionRegsPage,
    onSelectAuction,
    openAuctionFromHibidView,
    backToAuctionList,
    fetchUserLots,
    openLotsModal,
    updateRegistrationPickup,
    updateRegistrationShipped,
    updateRegistrationDelivered,
    uploadRegistrationDeliveryProofPhotos,
    closeRegistrationFulfillmentConfirm,
    openRegistrationFulfillmentConfirm,
    confirmRegistrationFulfillmentChange,
    updateLotPickup,
    updateLotShipped,
    updateLotDelivered,
    resolveFileNameMismatchDialog,
    confirmAuctionFileNameMatch,
    uploadShippingXlsx,
    requestShippingUploadFile,
    uploadLotsCsv,
    uploadListernautFile,
    requestListernautUploadFile,
    openCreateAuctionDialog,
    createAuctionNow,
  };
}
